/**
 * LT-Estimate-UX-01 Section 7 — Tender Summary tab.
 *
 * VIEW ONLY. Nothing is editable here — every card carries a link/button
 * through to the tab where the underlying data lives. The 9 cards match
 * the user-locked spec order:
 *
 *   1  Cost composition        (labour / mats / plant / subby / prelims / provsums)
 *   2  Margin build-up         (INTERNAL: labour buffer, expected total)
 *   3  Labour by trade         (hours + charge value; internal buffer col)
 *   4  Provisional sums        (line-by-line, no percentages)
 *   5  A&O                     (assumptions / clarifications / omissions counts)
 *   6  Prelims                 (total + % + sanity-band flag)
 *   7  £/m²                    (needs GIA + base_rate + historical avg)
 *   8  Rough programme         (duration from labour_hours ÷ crew ÷ hrs/day)
 *   9  Pre-send checks         (blockers list, links straight to offenders)
 *
 * Card sizes intentionally vary to avoid a "uniform wall" — Card 2 spans
 * 2 columns, Card 3 spans 2, Card 9 (pre-send) spans full width when it
 * has blockers so the estimator can't miss it.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight, AlertTriangle, Check, Lock, ExternalLink,
  Clock, Users, ChevronRight, Info, Layers, FileText,
} from "lucide-react";
import PoundSterling from "lucide-react/dist/esm/icons/pound-sterling";
import Target from "lucide-react/dist/esm/icons/target";
import { toast } from "sonner";
import { useJobTypeLibrary } from "../hooks/useJobTypeLibrary";
import { api } from "../lib/api";
import { gbp, ukDate } from "../lib/format";

const num = (n) => (n == null ? "—" : Number(n).toLocaleString("en-GB", { maximumFractionDigits: 2 }));
const pct = (n) => (n == null ? "—" : `${Number(n).toFixed(1)}%`);
const cls = (...xs) => xs.filter(Boolean).join(" ");

/**
 * Small card shell. `internal` mode = amber left rule + "INTERNAL ONLY"
 * corner chip, matching the design system's guarded-content convention.
 */
function Card({
  title, subtitle, actions, children, cols = 1, rows = 1, internal = false,
  empty = false, testId,
}) {
  return (
    <div
      data-testid={testId}
      className={cls(
        "rounded-lg border bg-surface flex flex-col",
        internal ? "border-warning-soft" : "border-line",
      )}
      style={{
        gridColumn: `span ${cols}`, gridRow: `span ${rows}`,
        boxShadow: internal ? "inset 3px 0 0 #B45309" : undefined,
      }}
    >
      <header className="px-4 pt-3 pb-2 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-ink" style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              {title}
            </h3>
            {internal && (
              <span
                title="Internal only — never appears in client-facing exports"
                className="inline-flex items-center gap-1 rounded"
                style={{ background: "#FEF3E2", color: "#7A4C0A", border: "1px solid #F2DFB8", padding: "0 5px", fontSize: 9, fontWeight: 700, letterSpacing: "0.08em" }}
              >
                <Lock size={8} /> INTERNAL
              </span>
            )}
          </div>
          {subtitle && (
            <div className="text-ink-3 mt-0.5" style={{ fontSize: 11 }}>{subtitle}</div>
          )}
        </div>
        {actions}
      </header>
      <div className="px-4 pb-4 flex-1">
        {empty
          ? <div className="text-ink-3 italic py-4" style={{ fontSize: 12 }}>Nothing to show yet.</div>
          : children}
      </div>
    </div>
  );
}

function LinkOut({ to, label }) {
  const nav = useNavigate();
  return (
    <button
      type="button"
      onClick={() => nav(to)}
      data-testid={`summary-linkout-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
      className="inline-flex items-center gap-1 text-accent hover:text-accent-deep"
      style={{ fontSize: 11.5, fontWeight: 500 }}
    >
      {label} <ArrowRight size={11} />
    </button>
  );
}

// -------- CARDS ---------------------------------------------------------

function CardCostComposition({ data, tenderId }) {
  return (
    <Card
      title="Cost composition"
      subtitle="Net vs inc. O&P per bucket"
      actions={<LinkOut to={`/tenders/${tenderId}?tab=estimate`} label="Edit lines" />}
      cols={2} testId="summary-card-cost-composition"
    >
      <table className="w-full">
        <thead>
          <tr>
            <th className="col-head text-left pb-1.5">Bucket</th>
            <th className="col-head text-right pb-1.5">Net</th>
            <th className="col-head text-right pb-1.5">Inc. O&amp;P</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.key} className="border-t border-line-soft">
              <td className="py-1.5 text-ink" style={{ fontSize: 13 }}>{r.label}</td>
              <td className="py-1.5 text-right font-mono tnum text-ink-2" style={{ fontSize: 12.5 }}>{gbp(r.net)}</td>
              <td className="py-1.5 text-right font-mono tnum text-ink" style={{ fontSize: 12.5, fontWeight: 500 }}>{gbp(r.inc_oap)}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-line" style={{ background: "#F5F1E9" }}>
            <td className="py-2 text-ink" style={{ fontSize: 13, fontWeight: 700 }}>Total (cost basis)</td>
            <td className="py-2 text-right font-mono tnum text-ink" style={{ fontSize: 13, fontWeight: 700 }}>{gbp(data.cost_basis)}</td>
            <td className="py-2 text-right font-mono tnum text-ink" style={{ fontSize: 13, fontWeight: 700 }}>{gbp(data.cost_basis_inc)}</td>
          </tr>
          <tr>
            <td className="py-1.5 text-ink-3" style={{ fontSize: 12 }}>Client total</td>
            <td />
            <td className="py-1.5 text-right font-mono tnum text-accent-deep" style={{ fontSize: 13, fontWeight: 700 }}>{gbp(data.client_total)}</td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
}

function CardMarginBuildUp({ data, tenderId }) {
  return (
    <Card
      title="Margin build-up"
      subtitle="Cost → O&P → adjudication → total expected"
      cols={2} internal
      actions={<LinkOut to={`/tenders/${tenderId}?tab=adjudication`} label="Adjudicate" />}
      testId="summary-card-margin-build-up"
    >
      <table className="w-full">
        <tbody>
          <tr><td className="text-ink-2 py-1" style={{ fontSize: 13 }}>Cost basis</td><td className="text-right font-mono tnum text-ink py-1" style={{ fontSize: 13 }}>{gbp(data.cost_basis)}</td></tr>
          <tr><td className="text-ink-2 py-1" style={{ fontSize: 13 }}>Overhead {pct(data.overhead_pct)}</td><td className="text-right font-mono tnum text-ink py-1" style={{ fontSize: 13 }}>{gbp(data.overhead_gbp)}</td></tr>
          <tr><td className="text-ink-2 py-1" style={{ fontSize: 13 }}>Profit {pct(data.profit_pct)}</td><td className="text-right font-mono tnum text-ink py-1" style={{ fontSize: 13 }}>{gbp(data.profit_gbp)}</td></tr>
          <tr className="border-t border-line-soft"><td className="text-ink-2 py-1.5" style={{ fontSize: 13 }}>Subtotal</td><td className="text-right font-mono tnum text-ink py-1.5" style={{ fontSize: 13, fontWeight: 500 }}>{gbp(data.subtotal)}</td></tr>
          {data.final_adjustment !== 0 && (
            <tr><td className="text-ink-2 py-1" style={{ fontSize: 13 }}>Final adjustment</td><td className="text-right font-mono tnum text-ink py-1" style={{ fontSize: 13 }}>{gbp(data.final_adjustment)}</td></tr>
          )}
          <tr className="border-t-2 border-line" style={{ background: "#F5F1E9" }}>
            <td className="text-ink py-2" style={{ fontSize: 13, fontWeight: 700 }}>Client total</td>
            <td className="text-right font-mono tnum text-accent-deep py-2" style={{ fontSize: 13, fontWeight: 700 }}>{gbp(data.client_total)}</td>
          </tr>
          <tr><td colSpan={2} className="pt-3"><div className="col-head" style={{ color: "#7A4C0A" }}>Internal-only build-up</div></td></tr>
          <tr><td className="text-ink-2 py-1" style={{ fontSize: 13 }}>Adjudication margin</td><td className="text-right font-mono tnum text-ink py-1" style={{ fontSize: 13 }}>{gbp(data.adjudication_margin)}</td></tr>
          <tr><td className="text-ink-2 py-1" style={{ fontSize: 13 }}>Labour buffer (charge − pay)</td><td className="text-right font-mono tnum text-ink py-1" style={{ fontSize: 13 }}>{gbp(data.labour_buffer_total)}</td></tr>
          <tr className="border-t-2 border-warning-soft" style={{ background: "#FEF3E220" }}>
            <td className="py-2" style={{ fontSize: 13, fontWeight: 700, color: "#7A4C0A" }}>Total expected margin</td>
            <td className="text-right font-mono tnum py-2" style={{ fontSize: 13, fontWeight: 700, color: "#7A4C0A" }} data-testid="summary-total-expected-margin">
              {gbp(data.total_expected_margin)} <span className="text-ink-3" style={{ fontWeight: 400, fontSize: 11 }}>({pct(data.total_expected_pct_of_client)})</span>
            </td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
}

function CardLabourByTrade({ data, tenderId }) {
  return (
    <Card
      title="Labour by trade"
      subtitle={`${data.rows.length} trade${data.rows.length === 1 ? "" : "s"} — ${num(data.total_hours)}h · ${gbp(data.total_charge_value)}`}
      cols={2} internal
      actions={<LinkOut to={`/tenders/${tenderId}?tab=estimate`} label="Estimate" />}
      empty={data.rows.length === 0} testId="summary-card-labour-by-trade"
    >
      <table className="w-full">
        <thead>
          <tr>
            <th className="col-head text-left pb-1.5">Trade</th>
            <th className="col-head text-right pb-1.5">Hrs</th>
            <th className="col-head text-right pb-1.5">Charge £</th>
            <th className="col-head text-right pb-1.5" style={{ color: "#7A4C0A" }}>Buffer £</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r, i) => (
            <tr key={i} className="border-t border-line-soft">
              <td className="py-1.5 text-ink" style={{ fontSize: 13 }}>{r.trade_name}</td>
              <td className="py-1.5 text-right font-mono tnum text-ink-2" style={{ fontSize: 12.5 }}>{num(r.hours)}</td>
              <td className="py-1.5 text-right font-mono tnum text-ink" style={{ fontSize: 12.5, fontWeight: 500 }}>{gbp(r.charge_value)}</td>
              <td className="py-1.5 text-right font-mono tnum" style={{ fontSize: 12.5, color: (r.buffer_total || 0) < 0 ? "#B03A2E" : "#7A4C0A" }}>{gbp(r.buffer_total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function CardProvSums({ data, tenderId }) {
  return (
    <Card
      title="Provisional sums" subtitle={`${data.count} line${data.count === 1 ? "" : "s"} · ${gbp(data.total)} total`}
      actions={<LinkOut to={`/tenders/${tenderId}?tab=provsums`} label="Prov. Sums" />}
      empty={data.count === 0} testId="summary-card-provsums"
    >
      <ul className="space-y-1">
        {data.rows.map((r) => (
          <li key={r.id} className="flex items-baseline justify-between border-t border-line-soft py-1">
            <span className="text-ink truncate" style={{ fontSize: 12.5 }}>{r.name}</span>
            <span className="font-mono tnum text-ink flex-shrink-0" style={{ fontSize: 12.5, fontWeight: 500 }}>{gbp(r.amount)}</span>
          </li>
        ))}
        {data.count > 0 && (
          <li className="flex items-baseline justify-between border-t-2 border-line pt-2" style={{ background: "#F5F1E930" }}>
            <span className="text-ink" style={{ fontSize: 13, fontWeight: 700 }}>Total</span>
            <span className="font-mono tnum text-ink" style={{ fontSize: 13, fontWeight: 700 }}>{gbp(data.total)}</span>
          </li>
        )}
      </ul>
    </Card>
  );
}

function CardAOSummary({ data, tenderId }) {
  const c = data.counts;
  const total = c.assumptions + c.clarifications + c.omissions;
  return (
    <Card
      title="Assumptions, clarifications, omissions" subtitle={`${total} selected across A / C / O`}
      actions={<LinkOut to={`/tenders/${tenderId}?tab=ao`} label="A&O" />}
      empty={total === 0} testId="summary-card-ao"
    >
      <div className="grid grid-cols-3 gap-3">
        {[
          { key: "assumptions",    label: "Assumptions",    count: c.assumptions },
          { key: "clarifications", label: "Clarifications", count: c.clarifications },
          { key: "omissions",      label: "Omissions",      count: c.omissions },
        ].map((s) => (
          <div key={s.key} className="rounded-md border border-line-soft bg-surface-2/40 p-3">
            <div className="text-ink-3" style={{ fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase" }}>{s.label}</div>
            <div className="text-ink font-mono tnum mt-1" style={{ fontSize: 20, fontWeight: 700 }}>{s.count}</div>
            {s.count === 0 && (
              <div className="text-warning mt-1 inline-flex items-center gap-1" style={{ fontSize: 10.5 }}>
                <AlertTriangle size={10} /> zero {s.label.toLowerCase()} — worth a look
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

function CardPrelims({ data, tenderId }) {
  const band = data.band; // "ok" | "low" | "high"
  const bandLabel = band === "ok" ? "within band" : (band === "low" ? "below band — worth checking" : "above band — worth checking");
  const bandColor = band === "ok" ? "#3A5A47" : "#7A4C0A";
  return (
    <Card
      title="Prelims" subtitle={`${gbp(data.total)} · ${pct(data.pct_of_build_cost)} of build cost`}
      actions={<LinkOut to={`/tenders/${tenderId}?tab=prelims`} label="Prelims" />}
      testId="summary-card-prelims"
    >
      <div className="text-ink" style={{ fontSize: 12.5 }}>
        <div className="mb-2 inline-flex items-center gap-1.5" style={{ color: bandColor }}>
          {band === "ok" ? <Check size={12} /> : <AlertTriangle size={12} />}
          {bandLabel}
        </div>
        <div className="text-ink-3" style={{ fontSize: 11 }}>
          Reference band: {pct(data.band_bounds.low)} – {pct(data.band_bounds.high)}.
        </div>
      </div>
    </Card>
  );
}

function CardGbpPerM2({ data, tenderId }) {
  const hasGia = data.gia_m2 != null && data.gia_m2 > 0;
  const hasBase = data.base_rate_per_m2 != null && data.base_rate_per_m2 > 0;
  const hasHist = data.historical_avg_per_m2 != null;
  const hasJobType = !!data.job_type;
  // LT-15 — resolve the raw key to its library label for every display
  // site on this card. Archived types still render their label
  // (see hooks/useJobTypeLibrary for the fallback semantics).
  const { labelFor: jobTypeLabelFor } = useJobTypeLibrary();
  const jobTypeLabel = data.job_type ? jobTypeLabelFor(data.job_type) : "—";
  // Context-aware link — whichever is missing gets prioritised. Users
  // shouldn't have to guess which of the two prerequisites is absent.
  const linkTarget = !hasJobType
    ? { to: `/tenders/${tenderId}?tab=setup`, label: "Set job type" }
    : (!hasGia
        ? { to: `/tenders/${tenderId}?tab=adjudication`, label: "Set GIA" }
        : { to: `/tenders/${tenderId}?tab=adjudication`, label: "Adjust GIA" });
  return (
    <Card
      title="£ per m²"
      subtitle={data.job_type ? `Job type: ${jobTypeLabel}` : "Job type not set"}
      actions={<LinkOut to={linkTarget.to} label={linkTarget.label} />}
      testId="summary-card-gbp-per-m2"
    >
      {!hasJobType ? (
        <div className="rounded-md border border-warning-soft bg-warning-soft/30 p-3 text-warning-strong" style={{ fontSize: 12 }}>
          <AlertTriangle size={12} className="inline mr-1 -mt-0.5" />
          Set a job type on this tender to see £/m² and its benchmark. Job type controls both the historical average and the base rate lookup.
        </div>
      ) : !hasGia ? (
        <div className="rounded-md border border-warning-soft bg-warning-soft/30 p-3 text-warning-strong" style={{ fontSize: 12 }}>
          <AlertTriangle size={12} className="inline mr-1 -mt-0.5" />
          Add a floor area (GIA) to see £/m². Set it on the Adjudication tab under project size.
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-baseline justify-between">
            <span className="text-ink-2" style={{ fontSize: 12.5 }}>This tender</span>
            <span className="font-mono tnum text-ink" style={{ fontSize: 15, fontWeight: 700 }}>{gbp(data.per_m2)} / m²</span>
          </div>
          <div className="flex items-baseline justify-between border-t border-line-soft pt-1.5">
            <span className="text-ink-3" style={{ fontSize: 12 }}>Target ({jobTypeLabel})</span>
            <span className="font-mono tnum text-ink-2" style={{ fontSize: 13 }}>
              {hasBase ? `${gbp(data.base_rate_per_m2)} / m²` : (
                <span className="text-warning inline-flex items-center gap-1"><AlertTriangle size={10} /> not set</span>
              )}
            </span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-ink-3" style={{ fontSize: 12 }}>
              Historical avg ({jobTypeLabel})
              {hasHist && data.historical_sample_size ? ` — n=${data.historical_sample_size}` : ""}
            </span>
            <span className="font-mono tnum text-ink-2" style={{ fontSize: 13 }}>
              {hasHist
                ? `${gbp(data.historical_avg_per_m2)} / m²`
                : <span className="text-ink-4" style={{ fontSize: 11 }}>
                    need ≥ 3 closed tenders (currently {data.historical_sample_size || 0})
                  </span>}
            </span>
          </div>
          <div className="pt-2 text-ink-3" style={{ fontSize: 10.5 }}>
            GIA is {num(data.gia_m2)} m². Set base rates per job type in Settings.
          </div>
        </div>
      )}
    </Card>
  );
}

function CardRoughProgramme({ data }) {
  const noHours = !data.total_hours;
  return (
    <Card
      title="Rough programme"
      subtitle={data.summary || "Add labour hours to see indicative duration"}
      testId="summary-card-rough-programme"
    >
      {noHours ? (
        <div className="text-ink-3 italic" style={{ fontSize: 12 }}>
          No labour hours yet.
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <div className="text-ink-3" style={{ fontSize: 10.5, textTransform: "uppercase" }}>Hours</div>
            <div className="font-mono tnum text-ink" style={{ fontSize: 17, fontWeight: 700 }}>{num(data.total_hours)}</div>
          </div>
          <div>
            <div className="text-ink-3" style={{ fontSize: 10.5, textTransform: "uppercase" }}>Duration</div>
            <div className="font-mono tnum text-ink" style={{ fontSize: 17, fontWeight: 700 }}>{data.duration_weeks >= 1 ? `${Math.round(data.duration_weeks)}w` : `${Math.round(data.duration_days)}d`}</div>
          </div>
          <div>
            <div className="text-ink-3" style={{ fontSize: 10.5, textTransform: "uppercase" }}>Crew × hrs/day</div>
            <div className="font-mono tnum text-ink" style={{ fontSize: 17, fontWeight: 700 }}>{Math.round(data.crew_size)} × {data.hours_per_day}</div>
          </div>
        </div>
      )}
      <div className="mt-2 text-ink-3" style={{ fontSize: 10.5 }}>
        Crew size &amp; hours/day are configurable in Settings → Benchmarks.
      </div>
    </Card>
  );
}

function CardPreSendChecks({ data, tenderId, phasesRef }) {
  const blockers = data.blockers || [];
  const errors = blockers.filter((b) => b.severity === "block");
  const warns  = blockers.filter((b) => b.severity === "warn");
  const okState = errors.length === 0 && warns.length === 0;
  return (
    <Card
      title="Pre-send checks"
      subtitle={okState ? "All clear" : `${errors.length} blocker${errors.length === 1 ? "" : "s"} · ${warns.length} advisor${warns.length === 1 ? "y" : "ies"}`}
      cols={2} testId="summary-card-pre-send"
    >
      {okState ? (
        <div className="text-accent inline-flex items-center gap-1.5" style={{ fontSize: 13 }}>
          <Check size={14} /> Nothing outstanding. This tender is ready to review.
        </div>
      ) : (
        <ul className="space-y-1.5">
          {blockers.map((b, i) => (
            <li
              key={i}
              data-testid={`pre-send-check-${b.code}-${i}`}
              className={cls(
                "rounded-md border p-2 flex items-start gap-2",
                b.severity === "block"
                  ? "border-note-red/40 bg-note-red/10 text-note-red"
                  : "border-warning-soft bg-warning-soft/30 text-warning-strong"
              )}
              style={{ fontSize: 12.5 }}
            >
              <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                {b.message}
                {b.line_id && (
                  <button
                    type="button"
                    className="ml-2 underline"
                    onClick={() => {
                      const url = new URL(window.location.href);
                      url.searchParams.set("tab", "estimate");
                      url.searchParams.set("line", b.line_id);
                      window.location.href = url.toString();
                    }}
                  >
                    Jump to line
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// -------- PAGE ----------------------------------------------------------

export default function TenderOverview({ tender }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const tenderId = tender?.id;

  useEffect(() => {
    if (!tenderId) return;
    setLoading(true);
    api.get(`/tenders/${tenderId}/overview`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load tender overview"))
      .finally(() => setLoading(false));
  }, [tenderId]);

  if (loading) {
    return <div className="text-ink-4 py-8" style={{ fontSize: 12.5 }} data-testid="tender-overview-loading">Loading overview…</div>;
  }
  if (!data) {
    return <div className="text-note-red py-8" style={{ fontSize: 13 }}>Failed to load overview.</div>;
  }

  return (
    <div
      className="mt-6 grid gap-4"
      style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gridAutoRows: "min-content" }}
      data-testid="tender-overview-grid"
    >
      {/* Row 1 — cost composition (2) + margin build-up (2) */}
      <CardCostComposition data={data.cost_composition} tenderId={tenderId} />
      <CardMarginBuildUp   data={data.margin_build_up}   tenderId={tenderId} />

      {/* Row 2 — labour by trade (2) + provisional sums (1) + prelims (1) */}
      <CardLabourByTrade data={data.labour_by_trade} tenderId={tenderId} />
      <CardProvSums      data={data.provisional_sums} tenderId={tenderId} />
      <CardPrelims       data={data.prelims}          tenderId={tenderId} />

      {/* Row 3 — A&O (2) + £/m² (1) + programme (1) */}
      <CardAOSummary     data={data.ao_summary}       tenderId={tenderId} />
      <CardGbpPerM2      data={data.gbp_per_m2}       tenderId={tenderId} />
      <CardRoughProgramme data={data.rough_programme} />

      {/* Full-width pre-send checks — deliberately last so any blocker
          gets caught before the estimator navigates away from Summary. */}
      <div style={{ gridColumn: "span 4" }}>
        <CardPreSendChecks data={data.pre_send_checks} tenderId={tenderId} />
      </div>
    </div>
  );
}
